import React from 'react';
import { ShieldCheck, AlertTriangle, Info } from 'lucide-react';

export interface TierBadgeProps {
    determinism?: 'deterministic' | 'scored' | 'boundary' | string;
    size?: 'sm' | 'md';
    showIcon?: boolean;
    className?: string;
    customLabel?: string;
}

export const TIER_LABELS = {
    deterministic: 'Deterministic',
    scored: 'Scored',
    boundary: 'Data boundary',
} as const;

export function TierBadge({
    determinism,
    size = 'sm',
    showIcon = true,
    className = '',
    customLabel,
}: TierBadgeProps) {
    const isMedium = size === 'md';
    const textClasses = isMedium ? 'text-xs px-2.5 py-1' : 'text-[10px] px-2 py-0.5';
    const iconClasses = isMedium ? 'w-3.5 h-3.5' : 'w-3 h-3';

    if (determinism === 'deterministic') {
        return (
            <span
                className={`inline-flex items-center gap-1 font-bold uppercase rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 ${textClasses} ${className}`}
                title="Ground truth — registry match or court-grade deterministic record"
            >
                {showIcon && <ShieldCheck className={iconClasses} />}
                {customLabel || TIER_LABELS.deterministic}
            </span>
        );
    }

    if (determinism === 'scored') {
        return (
            <span
                className={`inline-flex items-center gap-1 font-bold uppercase rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 ${textClasses} ${className}`}
                title="Inference — probabilistic lead, cross-source harvest"
            >
                {showIcon && <AlertTriangle className={iconClasses} />}
                {customLabel || TIER_LABELS.scored}
            </span>
        );
    }

    return (
        <span
            className={`inline-flex items-center gap-1 font-bold uppercase rounded-full bg-secondary text-muted-foreground border border-border ${textClasses} ${className}`}
            title="Data boundary — unknown / not in registry"
        >
            {showIcon && <Info className={iconClasses} />}
            {customLabel || TIER_LABELS.boundary}
        </span>
    );
}

export default TierBadge;
